<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  name: 'ConfirmDialog',
});
</script>
<script setup lang="ts">
import { computed } from 'vue';
import { transformI18n } from '@klondpay/locales';

/**
 * 确认弹窗组件 (ConfirmDialog)
 * 带有警告图标的确认操作弹窗，适用于作废、删除等危险操作的二次确认。
 * 支持自定义标题、描述文字、确认按钮文字和取消按钮文字。
 */
interface Props {
  /** 是否显示弹窗 */
  visible: boolean;
  /** 弹窗标题 */
  title?: string;
  /** 主提示文字（加粗显示） */
  message?: string;
  /** 副提示文字（正常显示） */
  description?: string;
  /** 确认按钮文字 */
  confirmText?: string;
  /** 取消按钮文字 */
  cancelText?: string;
  /** 确认按钮颜色 */
  confirmColor?: string;
  /** 弹窗宽度 */
  width?: string;
  /** 确认按钮加载状态 */
  loading?: boolean;
}

;

const props = withDefaults(defineProps<Props>(), {
  title: '确认',
  message: '',
  description: '',
  confirmText: '确认',
  cancelText: '取消',
  confirmColor: '#f56c6c',
  width: '500px',
  loading: false,
});

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'confirm'): void;
  (e: 'cancel'): void;
}>();

/** 控制弹窗显隐 */
const dialogVisible = computed({
  get: () => props.visible,
  set: (val: boolean) => emit('update:visible', val),
});

/** 国际化处理 */
const i18nTitle = computed(() => transformI18n(props.title));
const i18nMessage = computed(() => transformI18n(props.message));
const i18nDescription = computed(() => transformI18n(props.description));
const i18nConfirmText = computed(() => transformI18n(props.confirmText));
const i18nCancelText = computed(() => transformI18n(props.cancelText));

/** 确认按钮样式 */
const confirmBtnStyle = computed(() => ({
  backgroundColor: props.confirmColor,
  borderColor: props.confirmColor,
  color: '#fff',
}));

function handleConfirm() {
  emit('confirm');
}

function handleCancel() {
  dialogVisible.value = false;
  emit('cancel');
}
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="i18nTitle"
    :width="width"
    :show-close="false"
    :close-on-click-modal="false"
    class="confirm-dialog"
    align-center
  >
    <div class="confirm-dialog__body">
      <!-- 警告图标 -->
      <div class="confirm-dialog__icon">
        <svg
          viewBox="0 0 24 24"
          width="40"
          height="40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="12" fill="#F56C6C" />
          <text x="12" y="17" text-anchor="middle" font-size="16" font-weight="bold" fill="#fff">
            !
          </text>
        </svg>
      </div>

      <!-- 主提示文字 -->
      <div v-if="i18nMessage" class="confirm-dialog__message">
        {{ i18nMessage }}
      </div>

      <!-- 副提示文字 -->
      <div v-if="i18nDescription" class="confirm-dialog__description">
        {{ i18nDescription }}
      </div>

      <!-- 自定义内容插槽 -->
      <slot />
    </div>

    <template #footer>
      <div class="confirm-dialog__footer">
        <el-button plain @click="handleCancel">
          {{ i18nCancelText }}
        </el-button>
        <el-button :style="confirmBtnStyle" :loading="loading" @click="handleConfirm">
          {{ i18nConfirmText }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.confirm-dialog__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 20px 30px;
}

.confirm-dialog__icon {
  margin-bottom: 16px;
}

.confirm-dialog__message {
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  text-align: center;
}

.confirm-dialog__description {
  font-size: 14px;
  line-height: 1.6;
  color: var(--el-text-color-regular);
  text-align: center;
}

.confirm-dialog__footer {
  display: flex;
  gap: 24px;
  justify-content: center;
  padding-bottom: 22px;

  .el-button {
    width: 102px;
    height: 32px;
    padding: 0;
  }
}
</style>
